import React, { useRef, useState } from 'react';
import { useAuth } from '@clerk/clerk-react';
import { authorButton, authorInput, authorPanel, downloadText, procedureAuthorPayload, procedureTemplate } from './qmsAuthoring';

const API_BASE = process.env.REACT_APP_API_URL;
const BOM_TEMPLATE = { schemaVersion:1, records:[{ documentNumber:'', partNumber:'', revision:'A1', kind:'MON', name:'', productFamily:'', productNumbers:[], rawHeaders:[], columnMapping:[],
  items:[{ itemNumber:'', ninPartNumber:'', revision:'', gpvPartNumber:'', description:'', quantity:null, unit:'', manufacturerPartNumber:'', manufacturer:'', isProcess:false, parentItemNumber:'', attributes:[], rawValues:[], references:[] }],
  references:[], provenance:{ sourceSystem:'', sourceUrl:'', sourceRevision:'', sourceHash:'' }, openPoints:[] }] };

export default function QmsJsonImport({ kind, onCreated }) {
  const { getToken } = useAuth();
  const [text, setText] = useState('');
  const [preview, setPreview] = useState(null);
  const [error, setError] = useState('');
  const [notice, setNotice] = useState('');
  const [busy, setBusy] = useState(false);
  const busyRef = useRef(false);
  const validated = useRef(null);
  const fileSequence = useRef(0);
  const batch = kind === 'bom';
  const endpoint = batch ? 'boms/import' : kind === 'assembly' ? 'assembly-instructions' : 'test-library';
  const change = value => { setText(value); setPreview(null); validated.current = null; setError(''); setNotice(''); };
  const newRequestId = () => window.crypto.randomUUID();

  async function request(payload, dryRun, requestId) {
    const token = await getToken();
    const res = await window.fetch(`${API_BASE}/quality/${endpoint}${!batch && dryRun ? '?dryRun=true' : ''}`, {
      method:'POST', headers:{ Authorization:`Bearer ${token}`, 'Content-Type':'application/json' },
      body:JSON.stringify(batch ? { records:payload.records, requestId, dryRun } : payload),
    });
    const data = await res.json();
    if (!res.ok || !data.ok) throw new Error(data.message || data.error || (data.errors || []).join('; ') || 'Import failed');
    return data;
  }

  async function validate() {
    if (busyRef.current) return;
    busyRef.current = true; setBusy(true); setError(''); setPreview(null); validated.current = null;
    try {
      let payload = JSON.parse(text);
      if (!payload || typeof payload !== 'object' || Array.isArray(payload)) throw new Error('Use one JSON object.');
      if (batch) {
        if (!Array.isArray(payload.records) || !payload.records.length || payload.records.length > 50) throw new Error('Provide 1–50 BOMs in records.');
      } else payload = procedureAuthorPayload(payload);
      const requestId = batch ? newRequestId() : undefined;
      const data = await request(payload, true, requestId);
      const valid = batch ? !(data.errors || []).length && !(data.summary?.conflict || data.summary?.invalid)
        && (data.records || []).every(r => r.action === 'create' || r.action === 'unchanged') : data.valid;
      setPreview(data);
      if (valid) validated.current = { payload, requestId };
    } catch (err) { setError(err.message); }
    finally { busyRef.current = false; setBusy(false); }
  }

  async function create() {
    if (busyRef.current || !validated.current) return;
    busyRef.current = true; setBusy(true); setError('');
    try {
      const { payload, requestId } = validated.current;
      const data = await request(payload, false, requestId);
      validated.current = null; setPreview(null); setText('');
      setNotice(batch ? 'BOM draft import completed. Existing records were preserved.' : 'Draft created.');
      onCreated?.(data);
    } catch (err) { setError(err.message); }
    finally { busyRef.current = false; setBusy(false); }
  }

  async function readFile(event) {
    const file = event.target.files?.[0];
    const sequence = ++fileSequence.current;
    if (!file) return;
    change('');
    if (file.size > 4 * 1024 * 1024) { setError('The JSON file must be smaller than 4 MB.'); return; }
    try { const value = await file.text(); if (sequence === fileSequence.current) change(value); }
    catch { if (sequence === fileSequence.current) setError('Could not read this file.'); }
  }

  return <details style={authorPanel}>
    <summary style={{ cursor:'pointer', fontWeight:700 }}>Import {batch ? 'BOM drafts' : 'a procedure draft'} from JSON</summary>
    <p style={{ fontSize:13, color:'var(--ni-text-muted)' }}>{batch
      ? 'Preview the complete batch before creating drafts. Existing records are never replaced. Ordered columns, blank identities and pinned references are preserved.'
      : 'Upload or paste one complete document. Validate first, then create an A1 Draft. Approval and revision history are assigned by QMS.'}</p>
    <div style={{ display:'flex', gap:10, flexWrap:'wrap', marginBottom:10 }}>
      <label>JSON file <input type="file" accept=".json,application/json" onChange={readFile} disabled={busy} /></label>
      <button type="button" style={authorButton} onClick={() => downloadText(JSON.stringify(batch ? BOM_TEMPLATE : procedureTemplate(kind), null, 2), `${kind}-draft-template.json`)}>Download JSON template</button>
    </div>
    <label style={{ display:'block' }}>Draft JSON<textarea aria-label="Draft JSON" rows={7} value={text} disabled={busy}
      onChange={e => { fileSequence.current++; change(e.target.value); }} style={{ ...authorInput, fontFamily:'monospace', marginTop:6 }} /></label>
    <div style={{ display:'flex', gap:10, marginTop:10 }}>
      <button type="button" style={authorButton} disabled={busy || !text.trim()} onClick={validate}>{busy ? 'Working…' : 'Validate import'}</button>
      <button type="button" style={authorButton} disabled={busy || !validated.current} onClick={create}>Create {batch ? 'BOM drafts' : 'Draft'}</button>
    </div>
    {error && <p role="alert" style={{ color:'#EC2124' }}>{error}</p>}
    {notice && <p role="status">{notice}</p>}
    {preview && <div role="status" style={{ marginTop:12, fontSize:13 }}>
      {batch ? <>
        <p>{preview.summary?.create || 0} new; {preview.summary?.unchanged || 0} unchanged; {preview.summary?.conflict || 0} conflicts; {preview.summary?.invalid || 0} invalid.</p>
        <ul>{(preview.records || []).map((r,i) => <li key={i}>{r.documentNumber} {r.revision} — {r.action}
          {[...(r.errors || []), ...(r.warnings || [])].map((m,j) => <div key={j}>{m}</div>)}
          {(r.openPoints || []).length > 0 && <div>{r.openPoints.length} open points retained in draft.</div>}</li>)}</ul>
        {[...(preview.errors || []), ...(preview.warnings || [])].map((m,i) => <p key={i}>{m}</p>)}
      </> : <p>Validated: {preview.documentNumber}, revision {preview.revision}. Ready to create a Draft.</p>}
    </div>}
  </details>;
}
